'use client'

import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ImageGrid } from './ImageGrid'
import { ImageUploader } from './ImageUploader'
import { useImageManager } from './useImageManager'

interface ImageItem {
  url: string
  thumb: string
  caption?: string
}

interface Props {
  open: boolean
  onClose: () => void
  onSelect: (image: ImageItem) => void
  sellerId: string | number
}

const PAGE_SIZE = 12

export function ImageManager({
  open,
  onClose,
  onSelect,
  sellerId,
}: Props) {
  const {
    images,
    total,
    page,
    setPage,
    loading,
    reload,
  } = useImageManager(sellerId, PAGE_SIZE)

  const bodyRef = useRef<HTMLDivElement>(null)
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // 🔁 Al cambiar página, subimos el scroll del body
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 })
  }, [page])

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="
          max-w-5xl
          h-[90vh]
          flex
          flex-col
          overflow-hidden
        "
      >
        {/* =========================
           HEADER (FIJO)
        ========================= */}
        <DialogHeader className="shrink-0 px-6 pt-6">
          <DialogTitle>Administrador de imágenes</DialogTitle>
        </DialogHeader>

        {/* =========================
           BODY (SCROLL)
        ========================= */}
        <div
          ref={bodyRef}
          className="flex-1 overflow-y-auto px-6 py-4 space-y-6"
        >
          {/* Upload + legacy */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <ImageUploader
              sellerId={sellerId}
              onUploaded={(img) => {
                reload()
                onSelect(img)
              }}
            />
          </div>

          {/* Grid */}
          <ImageGrid
            images={images}
            loading={loading}
            onSelect={(img) => {
              onSelect(img)
              onClose()
            }}
          />
        </div>

        {/* =========================
           FOOTER (FIJO)
        ========================= */}
        <div className="shrink-0 border-t px-6 py-4 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>

          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Anterior
            </Button>

            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
